/**
 * CommentsTab — inbox of the newest IG comments across every @flaxitup post.
 * Filter unanswered vs all. Per comment: Claude drafts a reply in brand voice,
 * user edits + sends via Windsor (which flips the row to `sent`), or dismisses
 * ("ignore" for noise like emoji-only "🙌").
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCheck, Copy, ExternalLink, Loader2, MessageCircle, RefreshCw, Send, Sparkles, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/charts/EmptyState';
import { cn } from '@/lib/utils';
import { useIgComments, useUpdateComment, type IgComment } from '@/hooks/useIgComments';

export function CommentsTab() {
  const [filter, setFilter] = useState<'unanswered' | 'all'>('unanswered');
  const q = useIgComments(filter);
  const qc = useQueryClient();

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex gap-1">
          <Chip label="Unanswered" active={filter === 'unanswered'} onClick={() => setFilter('unanswered')} />
          <Chip label="All" active={filter === 'all'} onClick={() => setFilter('all')} />
        </div>
        <div className="text-2xs text-muted-foreground">
          Comments cache — refresh via the Windsor pull (external sync)
        </div>
      </div>

      {q.isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-24 w-full" />)}
        </div>
      ) : q.isError ? (
        <EmptyState title="Couldn't load comments" description={String(q.error)} />
      ) : (q.data ?? []).length === 0 ? (
        <EmptyState title="Nothing here" description={filter === 'unanswered' ? 'All caught up.' : 'No cached comments yet.'} />
      ) : (
        <div className="space-y-2">
          {(q.data ?? []).map((c) => (
            <CommentCard key={c.comment_id} c={c} onDone={() => qc.invalidateQueries({ queryKey: ['ig-comments'] })} />
          ))}
        </div>
      )}
    </div>
  );
}

function CommentCard({ c, onDone }: { c: IgComment; onDone: () => void }) {
  const [replyEdit, setReplyEdit] = useState(c.reply_text ?? '');
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const update = useUpdateComment();

  async function draft() {
    setDrafting(true); setErr(null);
    try {
      const r = await fetch('/api/content/comment-draft', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ comment_id: c.comment_id }),
      });
      const j = await r.json();
      if (!r.ok || j.error) throw new Error(j.error ?? `HTTP ${r.status}`);
      setReplyEdit(j.reply);
      onDone();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setDrafting(false);
    }
  }

  async function send() {
    if (!replyEdit.trim()) return;
    setSending(true); setErr(null);
    try {
      const r = await fetch('/api/content/comment-reply', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ comment_id: c.comment_id, message: replyEdit }),
      });
      const j = await r.json();
      if (!r.ok || j.error) throw new Error(j.error ?? j.detail ?? `HTTP ${r.status}`);
      onDone();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSending(false);
    }
  }

  async function copy() {
    await navigator.clipboard.writeText(replyEdit);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  const isSent = c.reply_status === 'sent';
  const isIgnored = c.reply_status === 'ignored';

  return (
    <Card className={cn(isSent && 'opacity-70', isIgnored && 'opacity-50')}>
      <CardContent className="space-y-3 p-3">
        <div className="flex items-start gap-2">
          <MessageCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5 text-2xs text-muted-foreground">
              <span className="font-medium text-foreground">
                {c.comment_username ? `@${c.comment_username}` : 'IG user'}
              </span>
              {c.comment_timestamp && (
                <span className="tabular-nums">
                  {new Date(c.comment_timestamp).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
              {c.media_permalink && (
                <a href={c.media_permalink} target="_blank" rel="noreferrer" className="ml-auto flex items-center gap-0.5 text-violet-500 hover:underline">
                  <ExternalLink className="h-3 w-3" /> post
                </a>
              )}
              <StatusPill status={c.reply_status} />
            </div>
            <p className="mt-0.5 whitespace-pre-wrap text-sm">{c.comment_text}</p>
          </div>
        </div>

        {!isIgnored && !isSent && (
          <div className="space-y-2 rounded-md border border-border/40 bg-muted/10 p-2">
            {c.reply_status === 'drafted' && (
              <div className="flex items-center gap-1 text-2xs text-violet-500">
                <Sparkles className="h-3 w-3" /> Claude draft{c.ai_reply_ms != null && ` · ${(c.ai_reply_ms / 1000).toFixed(1)}s`}
              </div>
            )}
            <textarea
              rows={2}
              value={replyEdit}
              onChange={(e) => setReplyEdit(e.target.value)}
              placeholder="Reply as @flaxitup…"
              className="w-full rounded-md border border-border/50 bg-background px-2.5 py-1.5 text-sm"
            />
            <div className="flex flex-wrap gap-1.5">
              <Button size="sm" variant="outline" onClick={draft} disabled={drafting}>
                {drafting ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1.5 h-3.5 w-3.5" />}
                {c.reply_status === 'drafted' ? 'Redraft' : 'Draft with Claude'}
              </Button>
              <Button size="sm" onClick={send} disabled={sending || !replyEdit.trim()}>
                {sending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1.5 h-3.5 w-3.5" />}
                Send reply
              </Button>
              <Button size="sm" variant="outline" onClick={copy} disabled={!replyEdit.trim()}>
                {copied ? <CheckCheck className="mr-1.5 h-3.5 w-3.5" /> : <Copy className="mr-1.5 h-3.5 w-3.5" />}
                {copied ? 'Copied' : 'Copy'}
              </Button>
              <div className="ml-auto">
                <Button size="sm" variant="ghost" onClick={() => update.mutate({ comment_id: c.comment_id, patch: { reply_status: 'ignored' } })}>
                  <X className="mr-1.5 h-3.5 w-3.5" /> Ignore
                </Button>
              </div>
            </div>
            {err && <div className="text-2xs text-rose-500">{err}</div>}
          </div>
        )}
        {isSent && c.reply_text && (
          <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-sm">
            <div className="flex items-center gap-1 text-2xs text-emerald-600 dark:text-emerald-400">
              <CheckCheck className="h-3 w-3" /> Sent {c.reply_sent_at && new Date(c.reply_sent_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </div>
            <p className="mt-1 whitespace-pre-wrap">{c.reply_text}</p>
          </div>
        )}
        {isIgnored && (
          <Button size="sm" variant="ghost" onClick={() => update.mutate({ comment_id: c.comment_id, patch: { reply_status: 'new' } })}>
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Un-ignore
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function Chip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full border px-3 py-1 text-xs transition-colors',
        active ? 'border-violet-500 bg-violet-500/10 text-foreground' : 'border-border/50 text-muted-foreground hover:text-foreground',
      )}
    >
      {label}
    </button>
  );
}

function StatusPill({ status }: { status: IgComment['reply_status'] }) {
  const cfg = {
    new: { l: 'new', cls: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
    drafted: { l: 'draft ready', cls: 'bg-violet-500/10 text-violet-500' },
    sent: { l: 'sent', cls: 'bg-emerald-500/10 text-emerald-500' },
    ignored: { l: 'ignored', cls: 'bg-muted text-muted-foreground' },
  }[status];
  return <span className={cn('rounded-full px-1.5 py-0.5 text-2xs', cfg.cls)}>{cfg.l}</span>;
}
